import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, FileText, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";
import { Reveal } from "./Reveal";

const typeValues = ["Investitore", "Proposta Immobile", "Partnership", "Altro"];

export const ContactSection = () => {
  const { t } = useLang();
  const typeLabels = t("ct_types");
  const [form, setForm] = useState({ name: "", email: "", phone: "", request_type: typeValues[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", request_type: typeValues[0], message: "" });
      toast.success(t("ct_toast_ok"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className="text-xs tracking-[0.35em] uppercase font-mono text-amber-400/90 mb-4">{t("ct_overline")}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-zinc-100">
            {t("ct_h1")} <span className="italic text-gold">{t("ct_h2")}</span>
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mt-6 max-w-md">{t("ct_text")}</p>
          <div className="mt-10 space-y-5 text-sm text-zinc-400">
            <p className="flex items-start gap-3">
              <MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              Piazza Quattro Novembre 4, 20124 Milano (MI)
            </p>
            <p className="flex items-center gap-3">
              <Mail size={16} className="text-[#D4AF37] shrink-0" /> capitalstone.milano@gmail.com
            </p>
            <p className="flex items-center gap-3">
              <Phone size={16} className="text-[#D4AF37] shrink-0" /> +39 333 116 0150
            </p>
            <p className="flex items-center gap-3">
              <FileText size={16} className="text-[#D4AF37] shrink-0" /> P.IVA 14417030963 · REA MI-2781885 · PEC capitalstone@pec.capitalstone.it
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-amber-500/15 bg-[#121216] p-6 sm:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-input-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder={t("ct_name")}
                className="input-lux"
              />
              <input
                data-testid="contact-input-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("ct_email")}
                className="input-lux"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder={t("ct_phone")}
                className="input-lux"
              />
              <select
                data-testid="contact-select-type"
                value={form.request_type}
                onChange={set("request_type")}
                className="input-lux appearance-none"
              >
                {typeValues.map((v, i) => (
                  <option key={v} value={v}>{typeLabels[i]}</option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="contact-textarea-message"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder={t("ct_message")}
              className="input-lux resize-none"
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="group flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.2em] uppercase font-semibold px-8 py-4 hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? t("ct_sending") : t("ct_submit")}
              <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            {sent && (
              <p data-testid="contact-success-toast" className="text-sm text-emerald-400 border border-emerald-400/25 bg-emerald-400/10 px-4 py-3">
                {t("ct_success")}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
